import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  GetPermissionListResultDto,
  IdentityRoleCreateDto,
  IdentityRoleDto,
  IdentityRoleUpdateDto,
  IdentityDatatableInput,
  UpdatePermissionsDto,
} from '@/api/types/identity';
import { PERMISSION_PROVIDER_ROLE } from '@/api/types/identity';

// Routes from ABP conventional-controller mapping of the Finkit custom
// Finkit.Roles.IdentityRoleAppService (the service the legacy Identity/Roles
// page calls as finkit.roles.identityRole.*). Verified against the access log:
//   GetListForDataTableAsync -> GET  /api/app/identity-role/for-data-table
//   GetAsync(id)             -> GET  /api/app/identity-role/{id}
//   CreateAsync(input)       -> POST /api/app/identity-role
//   UpdateAsync(id, input)   -> PUT  /api/app/identity-role/{id}
//   DeleteAsync(id)          -> DELETE /api/app/identity-role/{id}
// (The legacy index.js deletes via ABP's /api/identity/roles/{id}; both reach
//  the same IdentityRoleManager domain logic. We use the Finkit service used by
//  the datatable for consistency.)
const BASE = '/api/app/identity-role';

// Volo.Abp.PermissionManagement conventional controller. Role permissions are
// addressed by role NAME (provider key "R"), not id.
const PERMISSIONS = '/api/permission-management/permissions';

export const identityRolesApi = {
  async getListForDataTable(
    input: IdentityDatatableInput,
  ): Promise<PagedResult<IdentityRoleDto>> {
    const { data } = await http.get<PagedResult<IdentityRoleDto>>(`${BASE}/for-data-table`, {
      params: input,
    });
    return data;
  },

  async get(id: string): Promise<IdentityRoleDto> {
    const { data } = await http.get<IdentityRoleDto>(`${BASE}/${id}`);
    return data;
  },

  async create(input: IdentityRoleCreateDto): Promise<IdentityRoleDto> {
    const { data } = await http.post<IdentityRoleDto>(BASE, input);
    return data;
  },

  async update(id: string, input: IdentityRoleUpdateDto): Promise<IdentityRoleDto> {
    const { data } = await http.put<IdentityRoleDto>(`${BASE}/${id}`, input);
    return data;
  },

  async remove(id: string): Promise<void> {
    await http.delete(`${BASE}/${id}`);
  },

  async getPermissions(roleName: string): Promise<GetPermissionListResultDto> {
    const { data } = await http.get<GetPermissionListResultDto>(PERMISSIONS, {
      params: { providerName: PERMISSION_PROVIDER_ROLE, providerKey: roleName },
    });
    return data;
  },

  async updatePermissions(roleName: string, input: UpdatePermissionsDto): Promise<void> {
    await http.put(PERMISSIONS, input, {
      params: { providerName: PERMISSION_PROVIDER_ROLE, providerKey: roleName },
    });
  },
};
